/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import { SkeletonWrapper } from '@/components/SkeletonWrapper';
import {
  IconArrowDownwardMS,
  IconArrowUpwardMS,
  IconMobileDataArrowsMS,
} from '@/constants/icons';
import {
  Table as StyledTable,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { NoDataTable } from '../NoDataTable';
import {
  StyledPagination,
  StyledTableCellBody,
  StyledTableCellHead,
} from './styles';

export type Order = 'asc' | 'desc';

export interface TableHeadData<T> {
  id?: number;
  label: string;
  key: keyof T;
  sortable?: boolean;
  width?: string;
  comparator?: (a: T, b: T, order: Order) => number;
}

interface TableProps<T> {
  data: T[] | [];
  tableHeadData: TableHeadData<T>[];
  emptyTableIcon?: ReactNode;
  emptyTableDescription?: string;
  renderRow: (row: T) => ReactNode;
  getRowId: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  pageSize?: number;
  records: number;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isLoading?: boolean;
  sortableKeys?: (keyof T)[];
  threeStateSort?: boolean;
  fixedLayout?: boolean;
  defaultOrderBy?: keyof T;
  defaultOrder?: Order;
}

export function Table<T>({
  data,
  tableHeadData,
  emptyTableIcon,
  emptyTableDescription,
  renderRow,
  getRowId,
  onRowClick,
  pageSize = 10,
  records,
  page,
  setPage,
  isLoading: externalLoading,
  sortableKeys,
  threeStateSort = false,
  fixedLayout = false,
  defaultOrderBy,
  defaultOrder = 'asc',
}: TableProps<T>): JSX.Element {
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [orderBy, setOrderBy] = useState<keyof T | null>(
    defaultOrderBy ?? null
  );
  const [internalLoading, setInternalLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setInternalLoading(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  const isLoading = externalLoading ?? internalLoading;

  function descendingComparator(a: T, b: T, orderBy: keyof T): number {
    const valueA = a[orderBy];
    const valueB = b[orderBy];

    const numA = Number(valueA);
    const numB = Number(valueB);

    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return numB - numA;
    }

    return String(valueB ?? '').localeCompare(String(valueA ?? ''));
  }

  function getComparator(
    order: Order,
    orderBy: keyof T
  ): (a: T, b: T) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const handleSort = (property: keyof T): void => {
    if (threeStateSort && orderBy === property && order === 'desc') {
      setOrder('asc');
      setOrderBy(null);
      return;
    }

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    const { comparator } =
      tableHeadData.find((cell) => cell.key === orderBy) ?? {};

    return [...data].sort(
      comparator
        ? (a, b) => comparator(a, b, order)
        : getComparator(order, orderBy)
    );
  }, [data, order, orderBy, tableHeadData]);

  const pageCount = Math.ceil(records / pageSize);
  const showPagination = pageCount > 1;

  const renderTableBodyContent = (): ReactNode => {
    if (isLoading) {
      return Array.from({ length: 3 }).map((_, index) => (
        <TableRow key={index}>
          <StyledTableCellBody colSpan={tableHeadData.length}>
            <SkeletonWrapper
              isLoading={isLoading}
              variant="rectangular"
              height={64}
            />
          </StyledTableCellBody>
        </TableRow>
      ));
    }

    if (emptyTableIcon && emptyTableDescription && !data.length) {
      return (
        <TableRow>
          <StyledTableCellBody colSpan={tableHeadData.length}>
            <NoDataTable
              icon={emptyTableIcon}
              description={emptyTableDescription}
            />
          </StyledTableCellBody>
        </TableRow>
      );
    }

    return sortedData.map((row) => (
      <TableRow
        key={getRowId(row)}
        onClick={() => onRowClick?.(row)}
        className="table-row-body"
      >
        {renderRow(row)}
      </TableRow>
    ));
  };

  return (
    <TableContainer sx={{ marginTop: '16px' }}>
      <StyledTable
        sx={fixedLayout ? { tableLayout: 'fixed', width: '100%' } : undefined}
      >
        <TableHead>
          <TableRow>
            {tableHeadData.map((cell) => {
              const isActive = orderBy === cell.key;
              const isSortable =
                !sortableKeys || sortableKeys.includes(cell.key);

              let arrow = IconMobileDataArrowsMS;

              if (isActive) {
                arrow =
                  order === 'asc' ? IconArrowUpwardMS : IconArrowDownwardMS;
              }

              return (
                <StyledTableCellHead
                  key={cell.id}
                  onClick={isSortable ? () => handleSort(cell.key) : undefined}
                  sx={{
                    ...(cell.width && { width: cell.width }),
                    ...(!isSortable && { cursor: 'default' }),
                  }}
                >
                  {cell.label}
                  {isSortable && <span>{!data.length ? '' : arrow}</span>}
                </StyledTableCellHead>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>{renderTableBodyContent()}</TableBody>
      </StyledTable>

      {showPagination && (
        <StyledPagination
          page={page}
          count={pageCount}
          onChange={(_, value) => setPage(value)}
          shape="rounded"
          color="standard"
        />
      )}
    </TableContainer>
  );
}
